import { FallbackIconCircle } from '@va/icons';
import { getOriginUrl } from '@va/util/helpers';
import classNames from 'classnames';
import { useEffect, useMemo, useState } from 'react';

type FaviconProps = {
  baseUrl?: string;
  size?: 'small' | 'medium' | 'large';
};

// VISA-9686
const FAVICON_EXCEPTIONS = ['localhost', 'mail.google.com'];

export function Favicon({ baseUrl, size = 'medium' }: FaviconProps) {
  const [isAvailable, setIsAvailable] = useState(false);

  const showFallback = useMemo(() => {
    if (!baseUrl) return true;
    return FAVICON_EXCEPTIONS.some((exception) => baseUrl.includes(exception));
  }, [baseUrl]);

  const sizeClassName = useMemo(
    () =>
      classNames({
        'w-22px h-22px': size === 'medium' || size === 'large',
        'w-16px h-16px': size === 'small',
      }),
    [size],
  );

  const url = baseUrl ? `${getOriginUrl(baseUrl)}/favicon.ico` : '';

  useEffect(() => {
    if (!url) return;

    const img = new Image();
    img.onload = () => {
      setIsAvailable(true);
    };
    img.onerror = () => {
      setIsAvailable(false);
    };
    img.src = url;
  }, [url]);

  if (!baseUrl || showFallback || !isAvailable) {
    return <FallbackIconCircle className={sizeClassName} />;
  }

  return (
    <object
      className={classNames('flex justify-center items-center overflow-hidden', sizeClassName)}
      data={url}
      type='image/x-icon'
      tabIndex={-1}
    >
      <FallbackIconCircle className={sizeClassName} />
    </object>
  );
}
